import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type {
  ActionResult,
  ParsedToolCall,
  Schedule,
  TaskSpec,
} from "@/types/fleet";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/* ------------------------------------------------------------------ *
 * Time
 * ------------------------------------------------------------------ */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Relative time in Traditional Chinese. `now` is passed in rather than read from
 * `Date.now()` so a table of 100 rows shares one ticking clock instead of
 * drifting row by row (see `ClockProvider`).
 */
export function timeAgo(t: string | number | Date | null | undefined, now = Date.now()): string {
  if (t === null || t === undefined || t === "") return "—";
  const dt = t instanceof Date ? t : new Date(t);
  const ms = dt.getTime();
  if (Number.isNaN(ms)) return "—";
  const s = Math.round((now - ms) / 1000);
  if (s < 0) return "剛剛";
  if (s < 10) return "剛剛";
  if (s < 60) return `${s} 秒前`;
  if (s < 3600) return `${Math.round(s / 60)} 分鐘前`;
  if (s < 86400) return `${Math.round(s / 3600)} 小時前`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)} 日前`;
  return dt.toLocaleDateString("zh-HK", { year: "numeric", month: "2-digit", day: "2-digit" });
}

/** Absolute local timestamp, for tooltips where "3 小時前" is not enough. */
export function absoluteTime(t: string | number | Date | null | undefined): string {
  if (t === null || t === undefined || t === "") return "—";
  const dt = t instanceof Date ? t : new Date(t);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("zh-HK", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export function clockTime(t: string | number | Date | null | undefined): string {
  if (t === null || t === undefined || t === "") return "—";
  const dt = t instanceof Date ? t : new Date(t);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleTimeString("zh-HK", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** A duration in ms as "1m 04s" / "940ms" / "2h 13m". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(Math.floor(s % 60)).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

/** Seconds as an approximate human span, for dispatch estimates. */
export function formatSeconds(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || Number.isNaN(sec)) return "—";
  if (sec < 60) return `${Math.round(sec)} 秒`;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} 分鐘`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} 小時 ${rest} 分鐘` : `${h} 小時`;
}

/* ------------------------------------------------------------------ *
 * Numbers
 * ------------------------------------------------------------------ */

/** 1,234,567 → "1.2M"; 23,456 → "23K"; 800 → "800". */
export function formatTokens(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 10_000) return Math.round(n / 1_000) + "K";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
}

export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US");
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatPercent(x: number | null | undefined, digits = 1): string {
  if (x === null || x === undefined || Number.isNaN(x)) return "—";
  return `${(x * 100).toFixed(digits)}%`;
}

export function truncate(s: string | null | undefined, n = 40): string {
  if (!s) return "—";
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

/* ------------------------------------------------------------------ *
 * Errors
 * ------------------------------------------------------------------ */

/**
 * The leading token of a run's `error`, used to group failures on the Overview.
 * Errors arrive as `E_TIMEOUT: model did not respond` or `TIMEOUT · step 12`;
 * both collapse to a single bucket the operator can act on.
 */
export function errorPrefix(error: string | null | undefined): string {
  if (!error) return "UNKNOWN";
  const head = error.trim().split(/[\s:·,(]/, 1)[0] ?? error;
  return head.replace(/^E_/, "").toUpperCase().slice(0, 32) || "UNKNOWN";
}

/* ------------------------------------------------------------------ *
 * Step payloads
 * ------------------------------------------------------------------ */

/**
 * `StepRecord.toolCallsJson` is "a JSON array of tool calls as returned by the
 * model" — the exact shape depends on which provider produced it, so parse
 * defensively and keep whatever names and arguments survive.
 */
export function parseToolCalls(json: string | null | undefined): ParsedToolCall[] {
  if (!json) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  return raw.map((item): ParsedToolCall => {
    if (typeof item !== "object" || item === null) return { tool: String(item), args: {} };
    const o = item as Record<string, unknown>;
    const tool =
      (typeof o.tool === "string" && o.tool) ||
      (typeof o.name === "string" && o.name) ||
      (typeof o.toolName === "string" && o.toolName) ||
      (typeof o.function === "object" &&
        o.function !== null &&
        typeof (o.function as Record<string, unknown>).name === "string" &&
        ((o.function as Record<string, unknown>).name as string)) ||
      "?";
    let args: Record<string, unknown> = {};
    const candidate =
      o.args ??
      o.arguments ??
      o.input ??
      (typeof o.function === "object" && o.function !== null
        ? (o.function as Record<string, unknown>).arguments
        : undefined);
    if (typeof candidate === "string") {
      try {
        const p: unknown = JSON.parse(candidate);
        if (p && typeof p === "object") args = p as Record<string, unknown>;
      } catch {
        args = { value: candidate };
      }
    } else if (candidate && typeof candidate === "object") {
      args = candidate as Record<string, unknown>;
    } else {
      // Some encoders inline the arguments next to the name.
      const { tool: _t, name: _n, toolName: _tn, function: _f, ...restArgs } = o;
      void _t;
      void _n;
      void _tn;
      void _f;
      args = restArgs;
    }
    return { tool, args };
  });
}

/** `StepRecord.resultsJson` — `{tool, ok, message, errorCode}` in execution order. */
export function parseResults(json: string | null | undefined): ActionResult[] {
  if (!json) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  return raw.map((item): ActionResult => {
    const o = (typeof item === "object" && item !== null ? item : {}) as Record<string, unknown>;
    return {
      tool: typeof o.tool === "string" ? o.tool : "?",
      ok: o.ok !== false,
      message: typeof o.message === "string" ? o.message : "",
      errorCode: typeof o.errorCode === "string" ? o.errorCode : null,
    };
  });
}

/**
 * One tool call rendered the way the operator reads it in a log:
 *   `tap [3]`, `type [2] "Alice" ⏎`, `open_app "WhatsApp"`, `scroll DOWN`.
 */
export function describeToolCall(call: ParsedToolCall): string {
  const a = call.args;
  const str = (k: string) => (typeof a[k] === "string" ? (a[k] as string) : undefined);
  const num = (k: string) => (typeof a[k] === "number" ? (a[k] as number) : undefined);
  const idx = num("index");
  const bracket = idx === undefined ? "" : ` [${idx}]`;

  switch (call.tool) {
    case "tap":
      return `tap${bracket}${a.long ? " (long)" : ""}`;
    case "tap_at":
      return `tap_at (${num("x") ?? "?"}, ${num("y") ?? "?"})${a.long ? " (long)" : ""}`;
    case "type": {
      const text = str("text") ?? "";
      return `type${bracket} ${JSON.stringify(truncate(text, 48))}${a.submit ? " ⏎" : ""}`;
    }
    case "scroll": {
      const amount = num("amount");
      return `scroll ${str("direction") ?? "?"}${bracket}${amount && amount !== 1 ? ` ×${amount}` : ""}`;
    }
    case "swipe":
      return `swipe (${num("x1") ?? "?"}, ${num("y1") ?? "?"}) → (${num("x2") ?? "?"}, ${num("y2") ?? "?"})`;
    case "press":
      return `press ${str("key") ?? "?"}`;
    case "open_app":
      return `open_app ${JSON.stringify(str("query") ?? "")}`;
    case "wait":
      return `wait ${num("seconds") ?? "?"}s`;
    case "look":
      return `look — ${truncate(str("reason"), 48)}`;
    case "extract":
      return `extract ${truncate(JSON.stringify(a.data ?? a), 64)}`;
    case "remember":
      return `remember ${JSON.stringify(truncate(str("note"), 48))}`;
    case "done":
      return `done — ${truncate(str("summary"), 64)}`;
    case "fail":
      return `fail — ${truncate(str("reason"), 64)}`;
    case "ask_user":
      return `ask_user — ${truncate(str("question"), 64)}`;
    default: {
      const keys = Object.keys(a);
      return keys.length ? `${call.tool} ${truncate(JSON.stringify(a), 64)}` : call.tool;
    }
  }
}

/* ------------------------------------------------------------------ *
 * Observation diffing
 * ------------------------------------------------------------------ */

export type DiffLine = { text: string; changed: boolean; lineNo: number };

/**
 * Mark the lines of `text` that are not present in `previous`.
 *
 * A screen observation is 40-odd lines of which ~30 repeat verbatim from the
 * previous step; a set-membership diff (rather than a real LCS) is enough to
 * grey them out, and it stays O(n) on a live tail.
 */
export function diffObservation(text: string, previous: string | null | undefined): DiffLine[] {
  const lines = text.split("\n");
  if (!previous) return lines.map((t, i) => ({ text: t, changed: true, lineNo: i + 1 }));
  const before = new Set(previous.split("\n").map((l) => l.trim()));
  return lines.map((t, i) => ({ text: t, changed: !before.has(t.trim()), lineNo: i + 1 }));
}

/* ------------------------------------------------------------------ *
 * Schedules
 * ------------------------------------------------------------------ */

export const WEEKDAY_LABELS = ["一", "二", "三", "四", "五", "六", "日"];

/**
 * The next `count` fire times for a schedule, computed locally so the editor can
 * show them before the task is saved. Times of day are local, matching the
 * device's own `AlarmScheduler`.
 */
export function nextFireTimes(schedule: Schedule, count = 3, from = Date.now()): Date[] {
  const out: Date[] = [];
  const { type, intervalMinutes, timesOfDay, daysOfWeek } = schedule;

  if (type === "NONE") return out;

  if (type === "INTERVAL") {
    const mins = intervalMinutes ?? 0;
    if (mins <= 0) return out;
    for (let i = 1; i <= count; i++) out.push(new Date(from + i * mins * MINUTE));
    return out;
  }

  const times = timesOfDay
    .map(parseHhMm)
    .filter((t): t is { h: number; m: number } => t !== null)
    .sort((a, b) => a.h * 60 + a.m - (b.h * 60 + b.m));
  if (!times.length) return out;

  const days = type === "WEEKLY" ? daysOfWeek.filter((d) => d >= 1 && d <= 7) : null;
  if (days && !days.length) return out;

  // Walk forward day by day; 60 days is more than enough for three fire times
  // even with a single weekly slot.
  const cursor = new Date(from);
  cursor.setHours(0, 0, 0, 0);
  for (let dayOffset = 0; dayOffset < 60 && out.length < count; dayOffset++) {
    const day = new Date(cursor.getTime() + dayOffset * DAY);
    if (days) {
      // JS getDay(): 0=Sun..6=Sat. ISO: 1=Mon..7=Sun.
      const iso = day.getDay() === 0 ? 7 : day.getDay();
      if (!days.includes(iso)) continue;
    }
    for (const t of times) {
      const at = new Date(day);
      at.setHours(t.h, t.m, 0, 0);
      if (at.getTime() > from) {
        out.push(at);
        if (out.length >= count) break;
      }
    }
  }
  return out;
}

export function parseHhMm(s: string): { h: number; m: number } | null {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(s.trim());
  if (!m) return null;
  return { h: Number(m[1]), m: Number(m[2]) };
}

/** Fires per hour per device — the multiplier behind the fan-out warning. */
export function firesPerHour(schedule: Schedule): number {
  switch (schedule.type) {
    case "NONE":
      return 0;
    case "INTERVAL": {
      const mins = schedule.intervalMinutes ?? 0;
      return mins > 0 ? 60 / mins : 0;
    }
    case "DAILY":
      return schedule.timesOfDay.length / 24;
    case "WEEKLY":
      return (schedule.timesOfDay.length * schedule.daysOfWeek.length) / (24 * 7);
  }
}

/** A one-line human summary of a schedule, for the task list. */
export function describeSchedule(s: Schedule): string {
  switch (s.type) {
    case "NONE":
      return "不排程";
    case "INTERVAL":
      return s.intervalMinutes ? `每 ${s.intervalMinutes} 分鐘` : "間隔未設定";
    case "DAILY":
      return s.timesOfDay.length ? `每日 ${s.timesOfDay.join("、")}` : "每日（未設時間）";
    case "WEEKLY": {
      const days = s.daysOfWeek
        .slice()
        .sort((a, b) => a - b)
        .map((d) => WEEKDAY_LABELS[d - 1] ?? String(d))
        .join("");
      return s.timesOfDay.length
        ? `每週${days || "?"} ${s.timesOfDay.join("、")}`
        : `每週${days || "?"}（未設時間）`;
    }
  }
}

/* ------------------------------------------------------------------ *
 * Misc
 * ------------------------------------------------------------------ */

export function totalTokens(r: {
  promptTokens: number;
  completionTokens: number;
  reasoningTokens: number;
}): number {
  return r.promptTokens + r.completionTokens + r.reasoningTokens;
}

/** Duration of a run, live-safe: an unfinished run measures against `now`. */
export function runElapsedMs(
  run: { startedAt: number | null; finishedAt: number | null; createdAt: number },
  now = Date.now(),
): number | null {
  const start = run.startedAt ?? run.createdAt;
  if (!start) return null;
  return (run.finishedAt ?? now) - start;
}

export function taskSummaryLine(t: TaskSpec): string {
  const bits = [describeSchedule(t.schedule), `${t.maxSteps} steps`];
  if (t.allowedPackages.length) bits.push(`${t.allowedPackages.length} apps`);
  if (t.extractionSchema) bits.push("schema");
  return bits.join(" · ");
}

/** Stable, dependency-free debounce for the editor fields. */
export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number) {
  let timer: number | undefined;
  const wrapped = (...args: A) => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), ms);
  };
  wrapped.cancel = () => {
    if (timer !== undefined) window.clearTimeout(timer);
  };
  return wrapped;
}

/**
 * `{{PLACEHOLDER}}` names a task spec uses, in reading order.
 *
 * Mirrors `taskspec.VARIABLE_RE` and `TEMPLATED_FIELDS` on the server. The form has to agree with
 * the dispatch guard about which variables exist, and deriving it here rather than trusting a
 * stored list means a goal edited in the textarea updates the fields as you type.
 */
const VARIABLE_RE = /\{\{\s*([A-Z][A-Z0-9_]*)\s*\}\}/g;
const TEMPLATED_FIELDS = ["name", "goal", "blockedLabelsRegex", "extractionSchema", "webhookUrl"] as const;

export function variablesIn(spec: {
  name?: string;
  goal?: string;
  blockedLabelsRegex?: string | null;
  extractionSchema?: string | null;
  webhookUrl?: string | null;
}): string[] {
  const found: string[] = [];
  for (const field of TEMPLATED_FIELDS) {
    const value = spec[field];
    if (typeof value !== "string") continue;
    for (const m of value.matchAll(VARIABLE_RE)) {
      if (!found.includes(m[1])) found.push(m[1]);
    }
  }
  return found;
}
